// ─── Chart time in US/Eastern ─────────────────────────────────────────────────
// ⚠⚠ lightweight-charts HAS NO TIME ZONES. It renders a UTCTimestamp as UTC, so
// the 09:30 open read 13:30 (14:30 in winter) on the axis and the crosshair, and
// a daily bar showed a meaningless clock time. The library's documented answer is
// to SHIFT each timestamp by its Eastern offset before it reaches the chart, so
// the chart's "UTC" is Eastern wall-clock time. Then the axis, the crosshair
// label and the day boundaries (ET midnight, not 20:00 ET) are all market time.
//
// ⚠ Shift ONLY at the chart boundary. Everything that matches real instants
// (marker alignment, signal times, marker tooltips) keeps using the real
// unix seconds; only the values handed to series and markers are shifted.
//
// ⚠ DST fall-back repeats 01:00–02:00 ET, so two real instants can shift to the
// same chart time, and lightweight-charts throws on a non-ascending series.
// chartTimes() drops any bar whose shifted time does not move forward. Stock
// sessions never trade that hour (it is a Sunday); only a 24h symbol could hit it.

const _etParts = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York", hourCycle: "h23",
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
});

// The Eastern offset only changes on the hour, so it is cached per UTC hour.
const _offsetByHour = new globalThis.Map();

function etOffsetSeconds(t) {
  const hour = Math.floor(t / 3600);
  const hit = _offsetByHour.get(hour);
  if (hit !== undefined) return hit;
  const p = {};
  for (const part of _etParts.formatToParts(new Date(hour * 3600 * 1000))) p[part.type] = part.value;
  const wall = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) / 1000;
  const off = wall - hour * 3600;           // -14400 (EDT) or -18000 (EST)
  _offsetByHour.set(hour, off);
  return off;
}

// Real unix seconds → chart seconds (Eastern wall clock expressed as UTC).
export function etChartTime(t) {
  if (typeof t !== "number" || !Number.isFinite(t)) return null;
  return t + etOffsetSeconds(t);
}

// Chart times for a bar array, index-aligned with it (indicator arrays are
// indexed by bar). null where the bar has no usable time or would not be
// strictly after the previous kept bar.
export function chartTimes(bars) {
  const out = new Array(bars?.length || 0).fill(null);
  let prev = -Infinity;
  for (let i = 0; i < out.length; i++) {
    const c = etChartTime(bars[i]?.time);
    if (c == null || c <= prev) continue;
    out[i] = c;
    prev = c;
  }
  return out;
}

// Daily or longer bars: the SMALLEST spacing is at least 20 hours. (The chart is
// not told its timeframe.) The minimum, not the median: 4h bars alternate 4h
// and overnight gaps, while consecutive daily bars are never under 23h apart,
// even across a DST change.
export function isDailyBars(bars) {
  const n = Math.min(bars?.length || 0, 60);
  if (n < 2) return false;
  let min = Infinity;
  for (let i = 1; i < n; i++) {
    const d = bars[i].time - bars[i - 1].time;
    if (Number.isFinite(d) && d > 0 && d < min) min = d;
  }
  return Number.isFinite(min) && min >= 20 * 3600;
}

// Labels for a CHART time (already shifted), so they format in UTC.
const _dateFmt = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" });
const _clockFmt = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

export function chartTimeLabel(chartT, daily) {
  if (typeof chartT !== "number" || !Number.isFinite(chartT)) return "";
  const d = new Date(chartT * 1000);
  return daily ? _dateFmt.format(d) : `${_dateFmt.format(d)} ${_clockFmt.format(d)} ET`;
}

const _px = (v) => {
  if (typeof v !== "number" || !Number.isFinite(v)) return "—";
  return Math.abs(v) >= 1 ? v.toFixed(2) : v.toPrecision(4);
};

// The crosshair readout: "Oct 22, 2026 09:47 ET · O 230.12 H 231.40 L 229.80 C 230.95".
export function readoutText(chartT, bar, daily) {
  const when = chartTimeLabel(chartT, daily);
  if (!when) return "";
  if (!bar) return when;
  return `${when} · O ${_px(bar.open)} H ${_px(bar.high)} L ${_px(bar.low)} C ${_px(bar.close)}`;
}
